// frontend/src/pages/PaymentCallback.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import './RefineryPayment.css';
import Swal from 'sweetalert2';

const RefineryPayment = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const [status, setStatus] = useState('verifying');
  const [reference, setReference] = useState('');
  const [loading, setLoading] = useState(true);
  const [countdown, setCountdown] = useState(5);
  const [sessionId, setSessionId] = useState('');


  const localStorageData = JSON.parse(localStorage.getItem('dcs_simulator_user_data'));
  

  useEffect(() => {
    // Get reference from URL (Paystack sends it)
    const ref = searchParams.get('reference') || searchParams.get('trxref');
    setReference(ref);

    if (ref) {
      verifyPayment(ref);
    } else {
      setStatus('error');
      setLoading(false);
    }
  }, [searchParams]);

  const verifyPayment = async (ref) => {
    try {
      const response = await fetch(`https://api.nktechhub.com/api/dcs/paystack/verify/${ref}`);
      const data = await response.json();

      console.log('Data:', data);

      if (data.status === 'active') {
        setStatus('success');
      } else {
        setStatus('failed');
      }
    } catch (error) {
      console.error('Verification error:', error);
      setStatus('failed');
    } finally {
      setLoading(false);
    }
  };

  // Auto-redirect to dashboard after success
  useEffect(() => {
  if (status === 'success' && countdown > 0) {
    const timer = setTimeout(() => {
      setCountdown(countdown - 1);
    }, 1000);
    return () => clearTimeout(timer);
  } else if (status === 'success' && countdown === 0) {
    // ✅ Create an async function inside useEffect
    const createSessionAndRedirect = async () => {
      try {
        const token = localStorage.getItem('token');
        // ✅ 1. Create session
        // const response = await fetch(`https://api.nktechhub.com/api/dcs/simulator/create-session`, {
        //   method: 'POST',
        //   headers: {
        //     'Content-Type': 'application/json',
        //   },
        //   body: JSON.stringify({ userData: localStorageData?.userData }),
        //   credentials: 'include'
        // });

        // if (!response.ok) {
        //   throw new Error(`HTTP error! status: ${response.status}`);
        // }

        // ✅ 2. Parse the response
        // const data = await response.json();
        // const { sessionId } = data;
        // if (sessionId){
        //   setSessionId(sessionId);
        // }
        
        // console.log('✅ Session created:', sessionId);

        // ✅ 3. Show success message
        Swal.fire({
          icon: 'success',
          title: 'Redirecting',
          text: 'Redirecting to Refinery Simulator...',
          timer: 2000,
          showConfirmButton: false,
        });

        // ✅ 4. Redirect to simulator with session ID
        // Use environment variable for flexibility
        const simulatorUrl = `https://refinery.nktechhub.com?token=${token}` || 'http://localhost:3001' || import.meta.env.VITE_REFINERY_SIMULATOR_URL ;
        window.location.href = `${simulatorUrl}?session=${sessionId}`;
        
      } catch (error) {
        console.error('❌ Error creating session:', error);
        
        // Show error message
        Swal.fire({
          icon: 'error',
          title: 'Redirect Failed',
          text: 'Could not create session. Please try again.',
          confirmButtonText: 'OK'
        });
        
        // Fallback: Redirect without session (might require login)
        window.location.href = 'https://refinery.nktechhub.com';
      }
    };

    // ✅ Call the async function
    createSessionAndRedirect();
  }
}, [status, countdown, navigate]);




  // Loading state
  if (loading) {
    return (
      <div className="payment-callback">
        <div className="payment-container">
          <div className="loader">
            <div className="spinner"></div>
            <h2>Verifying Your Payment...</h2>
            <p>Please wait while we confirm your transaction</p>
          </div>
        </div>
      </div>
    );
  }

  // Success state
  if (status === 'success') {
    return (
      <div className="payment-callback">
        <div className="payment-container success-container">
          <div className="icon-wrapper success-icon">
            <span className="icon">✅</span>
          </div>
          <h1 className="title">Payment Successful!</h1>
          <p className="subtitle">Thank you for your purchase</p>
          
          <div className="payment-details">
            <div className="detail-row">
              <span className="label">Transaction Reference</span>
              <span className="value reference">{reference}</span>
            </div>
            <div className="detail-row">
              <span className="label">Status</span>
              <span className="value status-badge success">Payment Complete</span>
            </div>
          </div>

          <div className="action-buttons">
            <button 
              className="btn btn-primary"
              onClick={() => window.location.href = `https://refinery.nktechhub.com?token=${localStorage.getItem('token')}`} //"https://refinery.nktechhub.com"}
            >
              Go to Dashboard
            </button>
            {/* <button 
              className="btn btn-secondary"
              onClick={() => navigate('/plans')}
            >
              View Plans
            </button> */}
          </div>

          <p className="redirect-message">
            Redirecting to dashboard in <strong>{countdown}</strong> seconds...
          </p>

          <div className="whats-next">
            <h3>What's Next?</h3>
            <ul>
              <li>📚 You now have access to your refinery simulation</li>
              {/* <li>📧 Check your email for confirmation</li> */}
              <li>💬 Contact support if you have any questions</li>
            </ul>
          </div>
        </div>
      </div>
    );
  }

  // Failed state
  if (status === 'failed') {
    return (
      <div className="payment-callback">
        <div className="payment-container failed-container">
          <div className="icon-wrapper failed-icon">
            <span className="icon">❌</span>
          </div>
          <h1 className="title">Payment Failed</h1>
          <p className="subtitle">We couldn't process your payment</p>
          
          <div className="payment-details">
            <div className="detail-row">
              <span className="label">Transaction Reference</span>
              <span className="value reference">{reference}</span>
            </div>
            <div className="detail-row">
              <span className="label">Status</span>
              <span className="value status-badge failed">Failed</span>
            </div>
          </div>

          <div className="failed-reasons">
            <h3>Possible Reasons:</h3>
            <ul>
              <li>Insufficient funds</li>
              <li>Incorrect card details</li>
              <li>Network timeout</li>
              <li>Bank declined the transaction</li>
            </ul>
          </div>

          <div className="action-buttons">
            <button 
              className="btn btn-primary"
              onClick={() => navigate('/')}
            >
              Try Again
            </button>
            <button 
              className="btn btn-secondary"
              onClick={() => window.location.href = 'mailto:natykinstech@gmail.com'}
            >
              Contact Support
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Error state (no reference)
  return (
    <div className="payment-callback">
      <div className="payment-container error-container">
        <div className="icon-wrapper error-icon">
          <span className="icon">⚠️</span>
        </div>
        <h1 className="title">Invalid Request</h1>
        <p className="subtitle">No payment reference found</p>
        <button 
          className="btn btn-primary"
          onClick={() => navigate('/')}
        >
          Go to Home
        </button>
      </div>
    </div>
  );
};

export default RefineryPayment;